import { z } from 'zod';
import { validatedGet, validatedPost, validatedPut, validatedPatch, validatedDelete } from '../api-client.ts';
import { DelegaSchema } from '@/types/entities/index.ts';
import { DelegaArricchitaSchema } from '@/types/api-responses.ts';
import type { CassaCode, MotivoArchiviazione, ModalitaRaccolta } from '@/types/entities/index.ts';
import { OkResult, buildQueryUrl } from './shared.ts';
import type { PaginatedParams } from './shared.ts';

const DelegheListResult = z.object({
  deleghe: z.array(DelegaArricchitaSchema),
  count: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});

const PresentaResult = z.object({
  filePath: z.string(),
  count: z.number(),
  codaId: z.number(),
  accorpata: z.boolean(),
});

const DelegaDettaglioSchema = DelegaSchema.extend({ operatoreNome: z.string().nullable().optional() });

/** Delega salvata (inserita o aggiornata), con l'esito della collocazione nell'altra cassa */
const DelegaCollocataSchema = DelegaSchema.extend({
  altraCassa: z.object({ spostate: z.number(), create: z.number() }).optional(),
});

const RiallineaResult = z.object({
  confermate: z.number(),
  archiviateConcorrente: z.number(),
  totale: z.number(),
  altraCassa: z.object({ spostate: z.number(), create: z.number() }),
  stati: z.object({ inValutazione: z.number(), inCessati: z.number() }),
});

interface ListaDelegheParams {
  [key: string]: string | number | undefined;
  page?: number;
  limit?: number;
  sort?: string;
  sortDir?: 'asc' | 'desc';
  q?: string;
  cassa?: string;
  stato?: string;
  da_archivio?: string;
}

function buildFiltriUrl(base: string, params: ListaDelegheParams): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  return `${base}${qs.toString() ? '?' + qs.toString() : ''}`;
}

export const delegheApi = {
  lista: (params: ListaDelegheParams = {}) =>
    validatedGet(buildFiltriUrl('deleghe/lista', params), DelegheListResult),

  presentate: (opts?: PaginatedParams) =>
    validatedGet(buildQueryUrl('deleghe/presentate', opts), DelegheListResult),

  daPresentare: (opts?: PaginatedParams) =>
    validatedGet(buildQueryUrl('deleghe/da-presentare', opts), DelegheListResult),

  daValutare: (opts?: PaginatedParams) =>
    validatedGet(buildQueryUrl('deleghe/da-valutare', opts), DelegheListResult),

  dettaglio: (id: number) =>
    validatedGet(`deleghe/${id}`, DelegaDettaglioSchema),

  crea: (dati: { lavoratoreId: number; cassa: CassaCode; dataDecorrenza?: string; operatore?: string; note?: string; coloreId: number; modalitaRaccolta: ModalitaRaccolta }) =>
    validatedPost('deleghe', dati, DelegaCollocataSchema),

  aggiorna: (id: number, dati: { cassa?: CassaCode; dataDecorrenza?: string | null; operatore?: string | null; note?: string | null; coloreId?: number; modalitaRaccolta?: ModalitaRaccolta | null }) =>
    validatedPut(`deleghe/${id}`, dati, DelegaCollocataSchema),

  presenta: (cassa: CassaCode, dataPresentazione: string, ids?: number[]) =>
    validatedPost('deleghe/presenta', { cassa, dataPresentazione, ...(ids?.length ? { ids } : {}) }, PresentaResult),

  riallinea: () =>
    validatedPost('deleghe/riallinea', {}, RiallineaResult),

  archivia: (id: number, motivo: MotivoArchiviazione, note?: string) =>
    validatedPatch(`deleghe/${id}/archivia`, { motivo, ...(note ? { note } : {}) }, OkResult),

  riattiva: (id: number, note: string) =>
    validatedPatch(`deleghe/${id}/riattiva`, { note }, DelegaSchema),

  elimina: (id: number) =>
    validatedDelete(`deleghe/${id}`, OkResult),

};
